import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ShoppingBag,
  CreditCard,
  QrCode,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  Clock,
  ArrowRight,
  Receipt,
  User,
  MapPin,
  Mail,
  Phone,
  Undo2,
} from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import {
  cn,
  formatBRL,
  formatDateTime,
  formatRelative,
  orderStatusColor,
} from '../lib/utils.js';

function paymentIcon(method: string) {
  if (method === 'pix') return <QrCode className="h-4 w-4" />;
  if (method === 'credit_card') return <CreditCard className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
}

function statusIcon(status: string) {
  if (status === 'approved') return <CheckCircle className="h-4 w-4" />;
  if (status === 'refunded' || status === 'chargeback')
    return <XCircle className="h-4 w-4" />;
  return <AlertCircle className="h-4 w-4" />;
}

const productKindLabel: Record<string, string> = {
  main: 'Principal',
  order_bump: 'Order Bump',
  upsell: 'Upsell',
  cross_sell: 'Cross-sell',
};

export function VendaDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundResult, setRefundResult] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['sale', id],
    queryFn: () => apiClient.saleDetail(id!),
    enabled: !!id,
  });

  const refundMut = useMutation({
    mutationFn: () =>
      apiClient.markRefunded(id!, {
        reason: refundReason.trim() || undefined,
      }),
    onSuccess: (r) => {
      setRefundOpen(false);
      setRefundReason('');
      setRefundResult(
        r.skipped
          ? `Já estava marcado: ${r.skipped}`
          : `Reembolso registrado · ${r.commissionsCancelled} commissions canceladas · JE reversa ${r.reverseJeId?.slice(0, 8) ?? '—'}…`,
      );
      qc.invalidateQueries({ queryKey: ['sale', id] });
      qc.invalidateQueries({ queryKey: ['dashboard-v2'] });
      qc.invalidateQueries({ queryKey: ['sales'] });
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
      qc.invalidateQueries({ queryKey: ['team-overview'] });
    },
    onError: (err) => {
      setRefundResult(`Erro: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Carregando pedido...</div>;
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Link
          to="/vendas"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para Vendas
        </Link>
        <div className="text-rose-400">Pedido não encontrado.</div>
      </div>
    );
  }

  const { order, journalEntries, upsellChain } = data;
  const grossCents = Number(order.amountGrossCents);
  const feeCents = Number(order.feeCents);
  const netCents = Number(order.netCents);

  return (
    <div className="p-6 space-y-6 max-w-[1200px] mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link
          to="/vendas"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Vendas
        </Link>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <span className="font-mono text-xs text-muted-foreground">
          {order.externalId}
        </span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <ShoppingBag className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">{order.productName}</h1>
            <span
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                orderStatusColor[order.status] ?? 'bg-slate-500/15 text-slate-400',
              )}
            >
              {statusIcon(order.status)}
              {order.status}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline" className="text-xs">
              {productKindLabel[order.productKind] ?? order.productKind}
            </Badge>
            <span>·</span>
            <span className="font-mono text-xs">{order.productCode}</span>
            <span>·</span>
            <span>{order.gateway}</span>
            <span>·</span>
            <span>{formatRelative(order.createdAt)}</span>
          </div>
        </div>
        <div className="text-right space-y-2">
          <div className="text-3xl font-bold tabular-nums">{formatBRL(grossCents)}</div>
          <div className="text-xs text-muted-foreground tabular-nums">
            líquido {formatBRL(netCents)}
          </div>
          {order.status === 'approved' && (
            <button
              type="button"
              onClick={() => { setRefundOpen(true); setRefundResult(null); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs font-medium hover:bg-amber-500/20"
              title="Registra reembolso manual: JE reversa + cancela commissions com clawback ativo + atualiza Saúde 30d"
            >
              <Undo2 className="h-3.5 w-3.5" />
              Marcar reembolsado
            </button>
          )}
          {order.status === 'refunded' && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-orange-500/10 text-orange-300 text-xs">
              <XCircle className="h-3.5 w-3.5" /> Reembolsada
            </div>
          )}
        </div>
      </div>

      {/* Modal refund (inline simple) */}
      {refundOpen && (
        <Card className="border-amber-500/50">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-bold">Marcar como reembolsada?</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Cria JE reversa (DR Receita · CR Saldo Ticto · CR Taxa) sem deletar a JE original (R5 — ledger append-only).
                  Cancela commissions pending/approved vinculadas. Emite event <code>payment.refunded</code> na timeline do cliente.
                  Atualiza Saúde 30d no Dashboard. <strong>Idempotente</strong> — rodar de novo não duplica.
                </div>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Motivo (opcional)</label>
              <input
                type="text"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="ex: chargeback cartão / arrependimento 7d / fraude"
                className="mt-1 w-full h-9 rounded-md border border-border bg-card px-3 text-sm"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setRefundOpen(false); setRefundReason(''); }}
                disabled={refundMut.isPending}
                className="px-3 py-1.5 rounded-md text-xs border border-border text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => refundMut.mutate()}
                disabled={refundMut.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 text-amber-950 text-xs font-bold hover:bg-amber-400 disabled:opacity-60"
              >
                {refundMut.isPending ? 'Processando…' : (
                  <>
                    <Undo2 className="h-3.5 w-3.5" /> Confirmar reembolso
                  </>
                )}
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {refundResult && (
        <div className={cn(
          'rounded-md border px-3 py-2 text-xs',
          refundResult.startsWith('Erro')
            ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
            : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
        )}>
          {refundResult}
        </div>
      )}

      {/* Status timeline */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  Criado
                </div>
                <div className="tabular-nums">{formatDateTime(order.createdAt)}</div>
              </div>
            </div>
            {order.approvedAt && (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <div>
                  <div className="text-[10px] text-emerald-500 uppercase tracking-wide">
                    Aprovado
                  </div>
                  <div className="tabular-nums">{formatDateTime(order.approvedAt)}</div>
                </div>
              </div>
            )}
            {order.scheduledReleaseAt && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                <div>
                  <div className="text-[10px] text-amber-500 uppercase tracking-wide">
                    Liberação prevista
                  </div>
                  <div className="tabular-nums">
                    {formatDateTime(order.scheduledReleaseAt)}
                  </div>
                </div>
              </div>
            )}
            {order.releasedAt && (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-500" />
                <div>
                  <div className="text-[10px] text-blue-500 uppercase tracking-wide">
                    Liberado
                  </div>
                  <div className="tabular-nums">{formatDateTime(order.releasedAt)}</div>
                </div>
              </div>
            )}
            {order.refundedAt && (
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-orange-500" />
                <div>
                  <div className="text-[10px] text-orange-500 uppercase tracking-wide">
                    Reembolsado
                  </div>
                  <div className="tabular-nums">{formatDateTime(order.refundedAt)}</div>
                </div>
              </div>
            )}
            {order.chargebackAt && (
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-rose-500" />
                <div>
                  <div className="text-[10px] text-rose-500 uppercase tracking-wide">
                    Chargeback
                  </div>
                  <div className="tabular-nums">{formatDateTime(order.chargebackAt)}</div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Cliente */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-4 w-4" /> Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Link
              to={`/contatos/${order.contactId}`}
              className="block group"
            >
              <div className="font-semibold group-hover:text-primary flex items-center gap-1">
                {order.contactName ?? '—'}
                <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100" />
              </div>
            </Link>
            {order.contactEmailReal && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-3.5 w-3.5" />
                <span className="truncate">{order.contactEmailReal}</span>
              </div>
            )}
            {order.contactPhoneReal && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="h-3.5 w-3.5" />
                <span>{order.contactPhoneReal}</span>
              </div>
            )}
            {(order.geoCity || order.geoState) && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                <span>{[order.geoCity, order.geoState].filter(Boolean).join(' / ')}</span>
              </div>
            )}
            <div className="pt-2 border-t border-border">
              <Link
                to={`/contatos/${order.contactId}`}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                Ver Customer 360 <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Pagamento */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {paymentIcon(order.paymentMethod)} Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Método</span>
              <span className="font-medium">{order.paymentMethod}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Parcelas</span>
              <span className="tabular-nums">{order.installments}x</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Gateway</span>
              <span>{order.gateway}</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="text-muted-foreground">External ID</span>
              <span className="font-mono text-xs">{order.externalId}</span>
            </div>
          </CardContent>
        </Card>

        {/* Valores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Valores
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Valor bruto</span>
              <span className="tabular-nums font-semibold">{formatBRL(grossCents)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Taxa gateway</span>
              <span className="tabular-nums text-rose-400">
                -{formatBRL(feeCents)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="font-medium">Líquido</span>
              <span className="tabular-nums font-bold text-emerald-400">
                {formatBRL(netCents)}
              </span>
            </div>
            {feeCents > 0 && (
              <div className="text-[10px] text-muted-foreground text-right pt-1">
                taxa efetiva: {((feeCents / grossCents) * 100).toFixed(2)}%
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upsell chain */}
      {upsellChain.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Cascata de Upsells ({upsellChain.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border/40">
              {upsellChain.map((u) => (
                <Link
                  key={u.id}
                  to={`/vendas/${u.id}`}
                  className="px-4 py-3 hover:bg-muted/30 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-[10px]">
                      {productKindLabel[u.productKind] ?? u.productKind}
                    </Badge>
                    <div>
                      <div className="text-sm font-medium">{u.productName}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {formatDateTime(u.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium',
                        orderStatusColor[u.status] ?? 'bg-slate-500/15 text-slate-400',
                      )}
                    >
                      {u.status}
                    </span>
                    <span className="tabular-nums text-sm font-medium">
                      {formatBRL(u.amountGrossCents)}
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Journal Entries vinculadas */}
      <Card>
        <CardHeader>
          <CardTitle>Reconciliação Contábil</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {journalEntries.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Nenhum lançamento contábil encontrado para este pedido.
              <div className="text-[10px] mt-1">
                Em produção, todo pedido aprovado gera JE automaticamente via webhook Ticto.
              </div>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {journalEntries.map((je) => (
                <Link
                  key={je.id}
                  to={`/financeiro/ledger/${je.id}`}
                  className="px-4 py-3 hover:bg-muted/30 flex items-center justify-between"
                >
                  <div>
                    <div className="text-sm font-medium">{je.description}</div>
                    <div className="text-[10px] text-muted-foreground">
                      competência {formatDateTime(je.competencia)} · postado{' '}
                      {formatRelative(je.postedAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="tabular-nums text-sm font-medium">
                      {formatBRL(je.totalDebitCents)}
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
